import React from "react";

import moment from "moment";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { formatPrice } from "@/src/utils/formatPrice";

import { getElementsByIndex } from "../../../../utils/data/Content";
import { getSubscriptionPayment } from "../../../../utils/data/Payment";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dateColumn,
  statusColumn,
} from "../../../dataTable/dataTableColumns";
import { SUBSCRIPTION_STATUS_TONES } from "../../../offers/tableParams/offerUserColumns";
import {
  transactionOfferColumn,
  transactionPaymentColumn,
} from "../../transactionAnalytics/tableParams/transactionColumns";

import type { TFunction } from "i18next";

import type { UserSubscriptionRow } from "./userSubscriptionColumns.types";
import type { UserSubscription } from "../../../../api/StatsAPI.types";
import type { FetchState } from "../../../../reducers/fetch.types";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../../dataTable/dataTable.types";

interface UserSubscriptionColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  offerAbility: boolean;
  hasPaypalProvider: boolean;
  pendingUuids: string[];
  onCancel: (subscriptionUuid: string, cancelAtPeriodEnd: boolean) => void;
}

const STATUS_LIST = [
  "active",
  "trialing",
  "processing",
  "waiting_iap_validation",
  "unpaid",
  "error",
  "incomplete",
  "incomplete_expired",
  "past_due",
  "canceled",
  "ended",
  "unknown",
] as const;

function getFrequencyLabel(
  subscription: UserSubscription,
  t: TFunction,
): string {
  if (subscription.frequency > 0) return String(subscription.frequency);

  if (subscription.payment_processor === "stripe_gift") {
    const duration = Math.round(
      moment
        .duration(
          moment(subscription.next_billing_date).diff(
            moment(subscription.subscription_date),
          ),
        )
        .asMonths(),
    );
    return t("gift", { duration });
  }

  return t("free_copy");
}

export function userSubscriptionRow(
  subscription: UserSubscription,
  offers: FetchState["offers"],
  t: TFunction,
): UserSubscriptionRow {
  const offer = getElementsByIndex(subscription.offer_uuid, offers, "uuid").at(
    0,
  );

  return {
    subscription_uuid: subscription.subscription_uuid,
    offer_uuid: subscription.offer_uuid,
    offer_label: offer?.label ?? "",
    payment: getSubscriptionPayment(subscription),
    retailer_name: subscription.retailer_name,
    status: subscription.status,
    frequency: subscription.frequency,
    frequency_label: getFrequencyLabel(subscription, t),
    price: subscription.price,
    price_label: formatPrice(subscription.price, subscription.currency) ?? "",
    subscription_date: subscription.subscription_date,
    next_billing_date: subscription.next_billing_date,
  };
}

export { getTransactionOfferHref as getUserSubscriptionHref } from "../../transactionAnalytics/tableParams/transactionColumns";

function isCancelable(row: UserSubscriptionRow): boolean {
  return (
    (row.payment === "okast" || row.payment === "stripe") &&
    row.status === "active" &&
    row.frequency > 0
  );
}

export function userSubscriptionColumns({
  t,
  projectSlug,
  offerAbility,
  hasPaypalProvider,
  pendingUuids,
  onCancel,
}: UserSubscriptionColumnsOptions): DataTableColumnDef<UserSubscriptionRow>[] {
  const getActions = (row: UserSubscriptionRow): RowAction[] => {
    if (!isCancelable(row)) return [];

    const isPending = pendingUuids.includes(row.subscription_uuid);
    const confirm = {
      title: t("deleteTitle"),
      description: t("deleteBody"),
      confirmLabel: t("deleteConfirm"),
    };

    return [
      {
        label: t("cancel_now"),
        onClick: () => onCancel(row.subscription_uuid, false),
        disabled: isPending,
        hidden: !offerAbility,
        variant: "destructive",
        confirm,
      },
      {
        label: t("cancel_at_end"),
        onClick: () => onCancel(row.subscription_uuid, true),
        disabled: isPending,
        hidden: !offerAbility,
        variant: "destructive",
        confirm,
      },
    ];
  };

  return [
    statusColumn<UserSubscriptionRow>(
      "status",
      t("status"),
      STATUS_LIST.map((value) => ({
        value,
        label: t(`subscription_status.${value}`),
        tone: SUBSCRIPTION_STATUS_TONES[value],
      })),
    ),
    transactionPaymentColumn<UserSubscriptionRow>(t, hasPaypalProvider),
    transactionOfferColumn<UserSubscriptionRow>(
      t("offer"),
      projectSlug,
      offerAbility,
    ),
    {
      id: "frequency",
      // Le tri porte sur le nombre de mois, pas sur le libellé.
      accessorFn: (row) => row.frequency,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("frequency_label")} />
      ),
      meta: { label: t("frequency_label"), width: "15%", minWidth: 185 },
      cell: ({ row }) => row.original.frequency_label,
    },
    {
      // Le tri porte sur le montant, pas sur le texte « prix devise ».
      accessorKey: "price",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      meta: { label: t("price"), width: "9%", minWidth: 105 },
      cell: ({ row }) => {
        const { price_label: priceLabel, payment } = row.original;
        if (!priceLabel) return null;

        return payment === "apple" ? (
          <span
            data-tooltip-content={t("go_to_apple_store")}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            {priceLabel}
          </span>
        ) : (
          priceLabel
        );
      },
    },
    dateColumn<UserSubscriptionRow>(
      "subscription_date",
      t("subscription_start_date"),
      { width: "19%", minWidth: 235 },
    ),
    dateColumn<UserSubscriptionRow>(
      "next_billing_date",
      t("subscription_end_date"),
      { width: "18%", minWidth: 220 },
    ),
    actionsColumn<UserSubscriptionRow>(getActions, (row) => row.offer_label),
  ];
}
